import type { ChangeEvent, MouseEvent } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Radio,
  Skeleton,
  Stack,
  SxProps,
  TableContainer,
  TableFooter,
  TableSortLabel,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import NoTableDataImage from "assets/images/NoDataImage.svg";
import EmptyState, {
  EmptyStateProps,
} from "global/components/EmptyState/EmptyState";
import CustomPagination from "./CustomPagination";
import { isTruthy } from "helpers/methods";
import customTableTokens from "./CustomTable.tokens";
import {
  emptyImageSx,
  emptyStateSx,
  paginationBarSx,
  selectedCountSx,
  tableCaptionSx,
  tableCardSx,
  tableContainerSx,
  tablePlainSx,
  tableSubtitleSx,
  tableSx,
  tableTitleSx,
} from "./CustomTable.styles";

interface CustomerListTableProps {
  headers?: any[];
  count?: number;
  rows?: any[];
  onDeselectAll?: () => void;
  onDeselectOne?: (id: any) => void;
  onPageChange?: (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  onRowsPerPageChange?: Function;
  onRowsSelect?: (event: MouseEvent<HTMLInputElement>) => void;
  onSelectAll?: () => void;
  onSelectOne?: (id: any) => void;
  page?: number;
  rowsPerPage?: number;
  selected?: any[];
  variant?: "primary" | "secondary";
  showPagination?: boolean;
  showJumpToPageNumber?: boolean;
  jumpToPageNumber?: number;
  setJumpToPageNumber?: Function;
  handleOnChangePageInput?: Function;
  caption?: JSX.Element | null;
  /** Accessible name for a row's selection control. Defaults to the row's
   *  `selectionLabel`, then name/title/email, else "row N", so each checkbox
   *  says what it selects. */
  getRowLabel?: (row: any, index: number) => string;
  showCheckbox?: boolean;
  onRowHover?: (index: number) => void;
  onRowLeave?: () => void;
  isLoading: boolean;
  handleSort?: (field: string) => void;
  sortConfig?: { field: string; order: Order; entity: string }[];
  radioSelection?: boolean;
  handleRadioClick?: any;
  selectedRadio?: string;
  setSelectedRadio?: any;
  radioHeader?: string;
  tableFooter?: JSX.Element | null;
  rowsPerPageOptions?: number[];
  isNoDataShow?: boolean;
  noDataComponent?: JSX.Element;
  customHeader?: any;
  hideSelectedCount?: boolean;
  highlightSelectedRows?: boolean;
  hideSortIcon?: boolean;
  title?: string;
  subtitle?: string;
  /**
   * Accessible name for the table when it has no visible caption. Prefer
   * `ariaLabelledBy` when a visible heading already names it (WCAG 1.3.1 / 4.1.2).
   */
  ariaLabel?: string;
  /** Id of a visible heading element that names this table. */
  ariaLabelledBy?: string;
  /**
   * Draws the shared card chrome (border, radius, shadow, white surface).
   * Turn it off only when the table already sits inside a card, dialog or
   * panel that supplies its own chrome — otherwise you get a card in a card.
   */
  card?: boolean;
  /** Message shown in place of rows when there is no data and no illustration. */
  emptyMessage?: string;
  /**
   * The shared empty-state block, drawn in place of the rows. Preferred over
   * `emptyMessage` — it names the absence, says how the list gets filled and
   * repeats the toolbar's action, where a bare sentence does none of that.
   * Never use it for a failed fetch: an empty state claims there is nothing
   * here, which is untrue when the request simply errored.
   */
  emptyState?: EmptyStateProps;
  /**
   * Height of a loading row. Only needed where a screen stacks two lines inside
   * a cell — the loader draws one bar per header, so it cannot infer that.
   */
  loadingRowHeight?: number | string;
  /**
   * Per-screen chrome overrides, merged *after* the shared rules so a screen
   * whose design genuinely differs can restate only what it needs.
   *
   * `cardSx` lands on the outer card, `tableSxOverride` on the `<Table>` — the
   * latter carries the same descendant selectors the base rule set uses
   * (`& .MuiTableHead-root .MuiTableCell-root`, …), so equal specificity plus
   * later source order is what makes it win. Use it only where the design says
   * so; every table without it stays on the canonical look.
   */
  cardSx?: SxProps<Theme>;
  tableSxOverride?: SxProps<Theme>;
}

type Order = "asc" | "desc";
const selectedRowHighlightSx = {
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: (theme: Theme) =>
      `${theme.palette.primary.alpha4} !important`,
  },
};

const t = customTableTokens;

const CustomTable = (props: CustomerListTableProps) => {
  const theme = useTheme();
  const {
    headers = [],
    count = 0,
    rows = [],
    onDeselectAll,
    onDeselectOne,
    onPageChange = () => {},
    onRowsPerPageChange,
    onSelectAll,
    onSelectOne,
    page = 0,
    rowsPerPage = 0,
    selected = [],
    onRowsSelect,
    onRowHover = () => {},
    onRowLeave,
  } = props;

  const selectedAll =
    selected.length > 0 && rows.every((row) => selected.includes(row.rowId));
  const selectedSome =
    selected.length > 0 &&
    rows.some((row) => selected.includes(row.rowId)) &&
    !selectedAll;

  const variant = props.variant ?? "primary";
  const showPagination = props.showPagination ?? true;
  const showCard = props.card ?? true;
  const rowsPerPageOptions = props.rowsPerPageOptions ?? [
    5, 10, 25, 50, 75, 100,
  ];

  const getHeadersData = () => {
    if (!props.showCheckbox && !props.radioSelection) {
      return headers;
    }

    const newHeaders = [];
    if (props.showCheckbox) {
      newHeaders.push({
        name: (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox
              checked={selectedAll}
              indeterminate={selectedSome}
              onChange={(event) => {
                if (event.target.checked) {
                  onSelectAll?.();
                } else {
                  onDeselectAll?.();
                }
              }}
              disabled={props.isLoading}
              inputProps={{ "aria-label": "Select all rows" }}
            />
            {!props.hideSelectedCount && isTruthy(selected) && (
              <Typography component="span" sx={selectedCountSx(theme)}>
                {selected.length} selected
              </Typography>
            )}
          </Stack>
        ),
        field: "checkbox",
        align: "left",
        sorting: false,
      });
    }

    if (props.radioSelection) {
      newHeaders.push({
        name: props.radioHeader,
        field: "radio",
        align: "left",
        sorting: false,
        width: "120px",
        sx: { whiteSpace: "nowrap" },
      });
    }

    return [...newHeaders, ...headers];
  };

  const getRowsData = () => {
    if (!Array.isArray(rows)) {
      return [];
    }

    if (!props.showCheckbox && !props.radioSelection) {
      return rows;
    }

    const newRows = rows.map((row, rowIndex) => {
      const isSelected = selected.includes(row.rowId);
      // WCAG 2.4.6 / 4.1.2: every "Select row" checkbox read the same, so a
      // screen-reader user could not tell which row each one selects.
      const plainLabel = [
        row.selectionLabel,
        row.name,
        row.title,
        row.fileName,
        row.emailSubject,
        row.email,
      ].find((v) => typeof v === "string" && v.trim());
      const rowLabel =
        props.getRowLabel?.(row, rowIndex) ??
        plainLabel ??
        `row ${rowIndex + 1}`;

      const rowData = {
        ...row,
        isSelected,
      };

      if (props.showCheckbox) {
        rowData.checkbox = (
          <Checkbox
            checked={isSelected}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              if (event.target.checked) {
                onSelectOne?.(row.rowId);
              } else {
                onDeselectOne?.(row.rowId);
              }
            }}
            value={isSelected}
            inputProps={{ "aria-label": `Select ${rowLabel}` }}
          />
        );
      }

      if (props.radioSelection) {
        rowData.radio = (
          <Radio
            checked={props.selectedRadio === row.rowId}
            onChange={() => {
              if (row.disableRadio) return;
              props.setSelectedRadio?.(row.rowId);
            }}
            value={row.rowId}
            size="small"
            disabled={row.disableRadio === true}
            inputProps={{ "aria-label": `Select ${rowLabel}` }}
          />
        );
      }
      return rowData;
    });

    return newRows;
  };

  const getHeaders = () => {
    const headers = getHeadersData();

    if (props.customHeader) {
      return (
        <TableHead>
          <TableRow>
            <TableCell colSpan={headers.length}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                {props.showCheckbox && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={selectedAll}
                      indeterminate={selectedSome}
                      onChange={(event) => {
                        if (event.target.checked) {
                          onSelectAll?.();
                        } else {
                          onDeselectAll?.();
                        }
                      }}
                      inputProps={{ "aria-label": "Select all rows" }}
                    />
                    {!props.hideSelectedCount && (
                      <Typography component="span" sx={selectedCountSx(theme)}>
                        {selected.length} selected
                      </Typography>
                    )}
                  </Stack>
                )}
                {props.customHeader}
              </Stack>
            </TableCell>
          </TableRow>
        </TableHead>
      );
    }

    return (
      <TableHead>
        <TableRow>
          {headers.map((column, index) => {
            const sortEntry = props.sortConfig?.find(
              (item) => item.field === column.field,
            );
            const isSortActive = Boolean(column.sorting && sortEntry);
            const sortDirection = sortEntry?.order ?? "asc";
            return (
              <TableCell
                align={column["align"]}
                key={index}
                width={column.width ?? "auto"}
                sx={column.sx}
                sortDirection={isSortActive ? sortDirection : undefined}
              >
                {!column.hide ? (
                  column.sorting ? (
                    <TableSortLabel
                      active={isSortActive}
                      direction={sortDirection}
                      onClick={() =>
                        props.handleSort && props.handleSort(column.field)
                      }
                      sx={
                        props.hideSortIcon
                          ? { "& .MuiTableSortLabel-icon": { display: "none" } }
                          : undefined
                      }
                    >
                      {column.name}
                    </TableSortLabel>
                  ) : column.icon ? (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={t.header.iconGap}
                    >
                      <Box
                        component="img"
                        src={column.icon}
                        width={t.header.iconSize}
                        alt={`${column.name} column icon`}
                        aria-hidden="true"
                      />
                      {column.name}
                    </Stack>
                  ) : (
                    <>{column.name}</>
                  )
                ) : null}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  };

  const getRowData = (row: any) => {
    const headers = getHeadersData();
    return headers.map((column, index) => (
      <TableCell align={column["align"]} key={index}>
        {row[column["field"]]}
      </TableCell>
    ));
  };

  /*
    The placeholder bar takes the row's own type metrics rather than a flat
    pixel height: a text `Skeleton` sizes its box from `fontSize` x line-height,
    so a fixed height made loading rows shorter than the rows that replaced
    them and the table shrank as the data landed. `loadingRowHeight` is for the
    tables whose cells stack two lines, which the header list alone can't reveal.
  */
  const TableRowsLoader = (headers: any[], rowsNum: number) => {
    return [...Array(rowsNum)].map((row, index) => (
      <TableRow key={index} sx={{ height: props.loadingRowHeight }}>
        {headers.map((header, headerIndex) => (
          <TableCell key={headerIndex}>
            <Skeleton
              animation="wave"
              variant="text"
              sx={{
                width: t.loading.skeletonWidth,
                fontSize: t.row.fontSize,
                lineHeight: t.row.lineHeight,
              }}
            />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  const getRowOnClickHandler = (row: any) =>
    onRowsSelect ? { onClick: () => onRowsSelect(row) } : {};

  const getEmptyState = () => (
    <TableRow>
      <TableCell colSpan={99} className={t.empty.cellClass}>
        {props.isNoDataShow ? (
          <Box sx={emptyStateSx(theme)}>{props.noDataComponent}</Box>
        ) : props.emptyState ? (
          <EmptyState {...props.emptyState} />
        ) : props.emptyMessage ? (
          <Box sx={emptyStateSx(theme)}>{props.emptyMessage}</Box>
        ) : (
          <Box sx={emptyStateSx(theme)}>
            <Box
              component="img"
              src={NoTableDataImage}
              alt="No data available"
              sx={emptyImageSx}
            />
          </Box>
        )}
      </TableCell>
    </TableRow>
  );

  const hasRows = rows?.length > 0;

  return (
    <Box
      sx={[
        showCard ? tableCardSx(theme) : tablePlainSx,
        ...(Array.isArray(props.cardSx) ? props.cardSx : [props.cardSx]),
      ]}
    >
      {(isTruthy(props.title) || isTruthy(props.subtitle)) && (
        <Box sx={tableCaptionSx}>
          {isTruthy(props.title) && (
            <Box component="span" sx={tableTitleSx(theme)}>
              {props.title}
            </Box>
          )}
          {isTruthy(props.subtitle) && (
            <Box component="span" sx={tableSubtitleSx(theme)}>
              {props.subtitle}
            </Box>
          )}
        </Box>
      )}
      <TableContainer sx={tableContainerSx}>
        <Table
          sx={[
            tableSx(theme),
            ...(Array.isArray(props.tableSxOverride)
              ? props.tableSxOverride
              : [props.tableSxOverride]),
          ]}
          aria-label={props.ariaLabel || props.title || undefined}
          aria-labelledby={props.ariaLabelledBy || undefined}
          aria-busy={props.isLoading || undefined}
        >
          {getHeaders()}
          <TableBody>
            {!props.isLoading && !hasRows && getEmptyState()}
            {props.isLoading
              ? TableRowsLoader(getHeadersData(), t.loading.rowCount)
              : getRowsData().map((row, index) => {
                  return (
                    <TableRow
                      hover
                      key={index}
                      selected={
                        props.highlightSelectedRows ? row.isSelected : undefined
                      }
                      sx={
                        props.highlightSelectedRows
                          ? selectedRowHighlightSx
                          : undefined
                      }
                      onMouseEnter={() => onRowHover(index)}
                      onMouseLeave={onRowLeave}
                      {...getRowOnClickHandler(row)}
                    >
                      {getRowData(row)}
                    </TableRow>
                  );
                })}
          </TableBody>
          {props.tableFooter && <TableFooter>{props.tableFooter}</TableFooter>}
        </Table>
      </TableContainer>
      {variant === "primary" &&
        !props.isLoading &&
        showPagination &&
        hasRows && (
          <Box sx={paginationBarSx(theme)}>
            <CustomPagination
              count={count}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              page={page}
              jumpToPageNumber={props.jumpToPageNumber}
              handleOnChangePageInput={props.handleOnChangePageInput}
              showJumpToPageNumber={props.showJumpToPageNumber}
              setJumpToPageNumber={props.setJumpToPageNumber}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={rowsPerPageOptions}
              shape="rounded"
            />
          </Box>
        )}
    </Box>
  );
};

export default CustomTable;
